import { motion } from 'framer-motion'
import { useSimStore } from '../store/simStore'
import { computeGrade } from '../lib/grade'

function AnimatedNumber({ value, decimals = 1 }: { value: number; decimals?: number }) {
  return (
    <motion.span
      key={value}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {value.toFixed(decimals)}
    </motion.span>
  )
}

export function ResultsScreen() {
  const { finalKpis, instanceConfig, selectedDispatcher, setAppState, resetRun } = useSimStore()
  const kpis = finalKpis ?? {}
  const spi = kpis.spi ?? 0
  const grade = computeGrade(spi)

  const handleRunAgain = () => {
    resetRun()
    setAppState('LOBBY')
  }

  const handleExport = () => {
    const rows = Object.entries(kpis).map(([k, v]) => `${k},${v}`).join('\n')
    const blob = new Blob([`metric,value\n${rows}`], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${instanceConfig?.name ?? 'sim'}_${selectedDispatcher}_results.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.15 } },
  }
  const item = {
    hidden: { opacity: 0, y: 20 },
    show:   { opacity: 1, y: 0 },
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#0d1117] text-white px-4">
      <motion.h2
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-3xl font-bold tracking-widest mb-1 text-white"
      >
        SIMULATION COMPLETE
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-slate-400 text-sm mb-8"
      >
        {instanceConfig?.name} · {selectedDispatcher} · T = {instanceConfig?.horizon} min
      </motion.p>

      {/* KPI cards */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
      >
        {[
          { label: 'Completed',  value: `${kpis.n_completed ?? 0} / ${kpis.n_total ?? 0}`, raw: null },
          { label: 'On-Time %',  value: null, raw: kpis.on_time_pct ?? 0, decimals: 1, suffix: '%' },
          { label: 'Mean Flow',  value: null, raw: kpis.mean_flow ?? 0, decimals: 1, suffix: ' min' },
          { label: 'SPI',        value: null, raw: spi, decimals: 3, suffix: '' },
        ].map((card) => (
          <motion.div
            key={card.label}
            variants={item}
            className="bg-[#161b22] border border-[#30363d] rounded-xl p-5 text-center min-w-32"
          >
            <div className="text-2xl font-bold font-mono text-teal-400">
              {card.value ?? (
                <><AnimatedNumber value={card.raw!} decimals={card.decimals} />{card.suffix}</>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-2">{card.label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Grade */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8, type: 'spring', stiffness: 200 }}
        className="mb-8 text-center"
      >
        <div className="text-lg text-slate-400 mb-1">Performance</div>
        <div className="flex items-center gap-3">
          {/* Stars */}
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <motion.span
                key={s}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: s <= grade.stars ? 1 : 0.2, scale: 1 }}
                transition={{ delay: 0.9 + s * 0.1, type: 'spring' }}
                className="text-2xl"
                style={{ color: s <= grade.stars ? '#f59e0b' : '#334155' }}
              >
                ★
              </motion.span>
            ))}
          </div>
          <span className="text-3xl font-bold" style={{ color: grade.colour }}>
            Grade {grade.grade}
          </span>
        </div>
        <div className="text-slate-400 text-sm mt-1">{grade.label}</div>
      </motion.div>

      {/* Action buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
        className="flex gap-4"
      >
        <button
          onClick={handleRunAgain}
          className="px-6 py-3 bg-teal-600 hover:bg-teal-500 rounded-xl font-bold text-black transition-all active:scale-95"
        >
          ▶ Run Again
        </button>
        <button
          onClick={() => { resetRun(); setAppState('LOBBY') }}
          className="px-6 py-3 bg-[#21262d] hover:bg-[#30363d] rounded-xl font-bold transition-all"
        >
          ⚙ Change Config
        </button>
        <button
          onClick={handleExport}
          className="px-6 py-3 bg-[#21262d] hover:bg-[#30363d] rounded-xl font-bold transition-all"
        >
          📊 Export Results
        </button>
      </motion.div>
    </div>
  )
}
